import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ToastNotification, {
  showToast,
} from "../../components/toasts/ToastNotification";
import { HashLoader } from "react-spinners";
import {
  deleteOrderForAdmin,
  getOrderForAdmin,
} from "../../api/order.api";
import "./admin-order-detail.css";
import { MdDelete } from "react-icons/md";
import { OrderStepper } from "../../components/stepper/OrderStepper";
import ConfirmModal from "../../components/modal/confirm-modal";
import { orderStatusStep } from "../../utils";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";
import { FaUserCircle } from "react-icons/fa";
import { FiPackage, FiTag } from "react-icons/fi";

export const AdminOrderDetail = () => {
  const { user } = useUser();
  const { orderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrder();
    document.title = `Đơn hàng: ${orderId}`;
  }, [orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await getOrderForAdmin(orderId);

      if (res?.success) {
        setOrder(res?.data?.order || null);
        setCurrentStep(orderStatusStep(res?.data?.order?.order_status));
      }
    } catch (error) {
      showToast(error?.message || "Không thể tải đơn hàng", "error");
    } finally {
      setLoading(false);
    }
  };

  const orderPackage = order?.product?.product_attributes?.packages?.find(
    (pack) => String(pack?._id) === String(order?.order_attributes?.packageId)
  );

  const formatMoney = (amount) => `${Number(amount || 0).toLocaleString()}đ`;

  const getStatusMeta = (status) => {
    const map = {
      success: {
        label: "Thành công",
        className: "admin-order-detail-status-success",
      },
      processing: {
        label: "Đang xử lý",
        className: "admin-order-detail-status-processing",
      },
      failed: {
        label: "Đã hủy",
        className: "admin-order-detail-status-failed",
      },
    };

    return (
      map[status] || {
        label: "Không xác định",
        className: "admin-order-detail-status-default",
      }
    );
  };

  const getTypeText = (type) => {
    if (type === "game_account") return "Tài khoản game";
    if (type === "topup_package") return "Gói nạp";
    if (type === "utility_account") return "Tài khoản tiện ích";
    if (type === "gift_card") return "Gift card";
    return "Khác";
  };

  const handleDeleteOrder = async () => {
    setLoading(true);
    setIsModalOpen(false);
    try {
      const res = await deleteOrderForAdmin(orderId);

      if (res?.success) {
        showToast("Xóa đơn hàng thành công", "success");
        navigate("/super-admin/orders");
      }
    } catch (error) {
      showToast(error?.message || "Không thể xóa đơn", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  const statusMeta = getStatusMeta(order?.order_status);

  return (
    <div className="admin-order-detail-page">
      <ToastNotification />
      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={() => handleDeleteOrder()}
        onClose={() => setIsModalOpen(false)}
        message={"Xác nhận bạn đang xóa một đơn hàng"}
        title={"Xóa đơn hàng"}
      />

      <section className="admin-order-detail-shell">
        <header className="admin-order-detail-header">
          <div>
            <p className="admin-order-detail-breadcrumb">
              <Link to="/super-admin/orders">Quản trị / Đơn hàng</Link> / {order?.orderId || "Chi tiết"}
            </p>
            <h1 className="admin-order-detail-title">
              Đơn hàng #{order?.orderId || "--"}
            </h1>
          </div>

          <div className="admin-order-detail-header-actions">
            <span className={`admin-order-detail-status ${statusMeta.className}`}>
              {statusMeta.label}
            </span>

            {hasPermission(user?.role, "delete:order") && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="admin-order-detail-delete-btn"
                title="Xóa đơn hàng"
              >
                <MdDelete />
                <span>Xóa đơn</span>
              </button>
            )}
          </div>
        </header>

        <section className="admin-order-detail-metrics-grid">
          <article className="admin-order-detail-metric-card">
            <span>Loại đơn</span>
            <strong>{getTypeText(order?.order_type)}</strong>
          </article>
          <article className="admin-order-detail-metric-card">
            <span>Tổng thanh toán</span>
            <strong>{formatMoney(order?.order_final_amount)}</strong>
          </article>
          <article className="admin-order-detail-metric-card">
            <span>Cập nhật gần nhất</span>
            <strong>
              {order?.updatedAt ? new Date(order.updatedAt).toLocaleString() : "--"}
            </strong>
          </article>
        </section>

        <div className="admin-order-detail-layout">
          <section className="admin-order-detail-main">
            <article className="admin-order-detail-card">
              <div className="admin-order-detail-card-head">
                <h3>Tiến trình đơn hàng</h3>
              </div>
              <div className="admin-order-detail-stepper-wrap">
                <OrderStepper
                  currentStep={currentStep}
                  order_status={order?.order_status}
                />
              </div>
            </article>

            <article className="admin-order-detail-card">
              <div className="admin-order-detail-card-head">
                <h3>Khách hàng</h3>
              </div>
              <div className="admin-order-detail-user-row">
                {order?.userId?.profileImg ? (
                  <img
                    className="admin-order-detail-user-avatar"
                    src={order?.userId?.profileImg}
                    alt={order?.userId?.username || "user"}
                  />
                ) : (
                  <span className="admin-order-detail-user-avatar-fallback">
                    <FaUserCircle />
                  </span>
                )}
                <div className="admin-order-detail-user-info">
                  <p className="admin-order-detail-user-name">
                    {order?.userId?.username || "--"}
                  </p>
                  <button
                    onClick={() =>
                      navigate(`/super-admin/users/view/${order?.userId?._id}`)
                    }
                    className="admin-order-detail-user-link-btn"
                  >
                    Xem hồ sơ người dùng
                  </button>
                </div>
              </div>
            </article>

            <article className="admin-order-detail-card">
              <div className="admin-order-detail-card-head">
                <h3>Sản phẩm</h3>
              </div>
              <div className="admin-order-detail-product-row">
                <img
                  className="admin-order-detail-product-image"
                  src={order?.product?.product_imgs?.[0]}
                  alt={order?.product?.product_name || "product"}
                />
                <div className="admin-order-detail-product-info">
                  <p className="admin-order-detail-product-name">
                    {order?.product?.product_name || "--"}
                  </p>
                  {order?.order_type === "topup_package" && (
                    <p className="admin-order-detail-attribute">
                      <FiPackage /> Gói: {orderPackage?.name || "--"}
                    </p>
                  )}

                  {order?.order_type === "game_account" &&
                    !order?.product?.isValuable && (
                      <>
                        <p className="admin-order-detail-attribute">
                          <FiTag /> Username: {order?.order_attributes?.username || "--"}
                        </p>
                        <p className="admin-order-detail-attribute">
                          <FiTag /> Password: {order?.order_attributes?.password || "--"}
                        </p>
                      </>
                    )}

                  {order?.order_type === "utility_account" &&
                    !order?.product?.isValuable &&
                    Array.isArray(order?.order_attributes) &&
                    order?.order_attributes?.length > 0 &&
                    order.order_attributes.map((od, idx) => (
                      <p
                        className="admin-order-detail-attribute"
                        key={`${od?.label || "attribute"}-${idx}`}
                      >
                        <FiTag /> {od?.label}: {od?.value}
                      </p>
                    ))}
                </div>
              </div>
            </article>
          </section>

          <aside className="admin-order-detail-side">
            <article className="admin-order-detail-card admin-order-detail-log-card">
              <div className="admin-order-detail-card-head">
                <h3>Nhật ký đơn hàng</h3>
              </div>

              <div className="admin-order-detail-log-list">
                <p className="admin-order-detail-log-item">
                  Tạo lúc:
                  <span>
                    {order?.createdAt ? new Date(order.createdAt).toLocaleString() : "--"}
                  </span>
                </p>
                <p className="admin-order-detail-log-item">
                  Cập nhật:
                  <span>
                    {order?.updatedAt ? new Date(order.updatedAt).toLocaleString() : "--"}
                  </span>
                </p>
                <p className="admin-order-detail-log-item">
                  Xử lý bởi:
                  <span>{order?.processed_by || "Hệ thống"}</span>
                </p>
                {!!order?.discountCode && (
                  <p className="admin-order-detail-log-item">
                    Mã giảm giá:
                    <span>{order?.discountCode}</span>
                  </p>
                )}
              </div>

              <div className="admin-order-detail-summary">
                <div className="admin-order-detail-summary-line">
                  <span>Tạm tính</span>
                  <span>{formatMoney(order?.order_base_amount)}</span>
                </div>
                <div className="admin-order-detail-summary-line">
                  <span>Giảm giá</span>
                  <span>-{Number(order?.order_discount_amount || 0).toLocaleString()}đ</span>
                </div>
                <div className="admin-order-detail-summary-total">
                  <strong>Đã thanh toán</strong>
                  <strong>{formatMoney(order?.order_final_amount)}</strong>
                </div>
              </div>
            </article>
          </aside>
        </div>
      </section>
    </div>
  );
};
